import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { download } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { LMSCourse } from '@nsc/simple-lms/entities/LMSCourse.js';
import { html, nothing } from 'lit';
import { exportFieldEngineersCsv } from '../../api/export-field-engineers-csv.js';
import { importFieldEngineersCsv } from '../../api/import-field-engineers-csv.js';

export const FieldEngineersImportExportPage = stateful(() => {
  const [fileName, setFileName] = useState<string>('');
  const [csvContent, setCsvContent] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null);

  const handleFileChange = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      setFileName('');
      setCsvContent('');
      return;
    }

    setFileName(file.name);
    const content = await file.text();
    setCsvContent(content);
  };

  const handleImport = async () => {
    if (!csvContent.trim()) return;

    setIsImporting(true);
    await guardDialog(
      async () => {
        const importMessages = await importFieldEngineersCsv(csvContent, selectedCourseId ?? undefined)();
        setMessages(importMessages ?? []);
      },
      { successText: 'Import complete.', errorMessagePrefix: 'Import failed' },
    ).finally(() => {
      setIsImporting(false);
    });
  };

  const handleExport = async () => {
    setIsExporting(true);
    await guardDialog(
      async () => {
        // The CSV is built server-side (see api/export-field-engineers-csv.ts) so we only ship the
        // finished file, not the full field-engineer/enrollment graph.
        const csv = await exportFieldEngineersCsv()();
        download(
          csv ?? '',
          `field-engineers-enrollments-${new Date().toISOString().split('T')[0]}.csv`,
          'text/csv',
        );
      },
      { successText: 'Export ready.', errorMessagePrefix: 'Export failed' },
    ).finally(() => {
      setIsExporting(false);
    });
  };

  return html`
    <section class="p-4 md:p-6 h-full overflow-y-auto">
      <div class="max-w-3xl mx-auto space-y-6">
        <!-- Import Card -->
        <div class="card bg-base-100 border border-base-300 shadow-sm">
          <div class="card-body gap-4">
            <h1 class="card-title text-2xl">Import Field Engineers</h1>
            <p class="text-base-content/70">
              Import field engineers from CSV. Use the provided FE import CSV template.
            </p>

            <div class="form-control gap-2">
              <label class="label py-0">
                <span class="label-text">CSV File</span>
              </label>
              <input
                class="file-input file-input-bordered w-full"
                type="file"
                accept=".csv,.txt,.tsv,text/csv,text/plain"
                @change=${handleFileChange}
              />
              ${fileName
                ? html`<span class="text-sm text-base-content/70">Selected file: ${fileName}</span>`
                : html`<span class="text-sm text-base-content/50">Use the provided FE import CSV template.</span>`}
            </div>

            <div class="form-control gap-2">
              <label class="label py-0">
                <span class="label-text">Assign imported users to course (optional)</span>
              </label>
              ${EntityPickerInput<typeof LMSCourse>({
                class: LMSCourse,
                filter: new AdornisFilter({ searchFields: ['title'] }),
                selectionSet: { _id: 1, title: 1 },
                placeholder: 'Select course (optional)',
                emptyState: 'No courses found',
                clearable: true,
                renderString: c => c.title ?? '',
                value: selectedCourseId,
                onEntityPicked: course => {
                  setSelectedCourseId(course?._id ?? null);
                },
              })}
              <span class="text-sm text-base-content/60">
                If selected, every newly imported field engineer will be enrolled in this course.
              </span>
            </div>

            <div class="card-actions justify-end">
              <button class="btn btn-primary" ?disabled=${!csvContent.trim() || isImporting} @click=${handleImport}>
                ${isImporting ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing} Import Field
                Engineers
              </button>
            </div>

            ${messages.length
              ? html`
                  <div class="rounded-box border border-base-300 bg-base-200 p-4">
                    <h2 class="font-semibold mb-2">Import Result</h2>
                    <ul class="list-disc pl-5 text-sm space-y-1">
                      ${messages.map(message => html`<li>${message}</li> `)}
                    </ul>
                  </div>
                `
              : nothing}
          </div>
        </div>

        <!-- Export Card -->
        <div class="card bg-base-100 border border-base-300 shadow-sm">
          <div class="card-body gap-4">
            <h2 class="card-title text-2xl">Export Field Engineers with Enrollments</h2>
            <p class="text-base-content/70">
              Export all field engineers with their course enrollments and scores as a CSV file.
            </p>

            <div class="card-actions justify-end">
              <button class="btn btn-secondary" ?disabled=${isExporting} @click=${handleExport}>
                ${isExporting ? html`<span class="loading loading-spinner loading-sm"></span>` : nothing} Export
                Engineers with Enrollments
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  `;
});
